import { useEffect, useState } from 'react';
import { Marked } from 'marked';
import DOMPurify from 'dompurify';

interface MarkdownProps {
  /** Public path to a .md file. */
  src: string;
  /** Drop the first level-1 heading (the title is shown in the header instead). */
  stripFirstH1?: boolean;
}

/**
 * Relative links/images in a .md file are authored relative to that file's
 * folder, but the browser would otherwise resolve them against the page URL.
 * Rewrite them to sit next to the markdown file. Absolute URLs, anchors,
 * `data:`/`mailto:` and protocol-relative URLs are left untouched.
 */
const resolveRelative = (url: string | undefined | null, base: string) => {
  if (!url) return url ?? '';
  if (/^([a-z][a-z0-9+.-]*:|\/\/|\/|#)/i.test(url)) return url;
  try {
    return new URL(url, new URL(base, window.location.origin)).pathname;
  } catch {
    return url;
  }
};

/**
 * Fetches a Markdown file and renders it as sanitized, theme-styled HTML.
 * Used by the AI portfolio detail view so case-study content lives in .md
 * files under public/, never in the codebase.
 */
const Markdown = ({ src, stripFirstH1 = false }: MarkdownProps) => {
  const [html, setHtml] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );

  useEffect(() => {
    let active = true;
    setStatus('loading');

    (async () => {
      try {
        const res = await fetch(src, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load markdown: ${res.status}`);
        let text = await res.text();
        if (stripFirstH1) {
          text = text.replace(/^\s*#\s+.*(?:\r?\n)+/, '');
        }
        // Directory of the markdown file, so relative asset paths resolve there.
        const baseDir = src.replace(/[^/]*$/, '');
        const marked = new Marked({
          gfm: true,
          walkTokens: (token) => {
            if (token.type === 'image' || token.type === 'link') {
              token.href = resolveRelative(token.href, baseDir);
            }
          },
        });
        const parsed = await marked.parse(text);
        if (active) {
          setHtml(DOMPurify.sanitize(parsed));
          setStatus('ready');
        }
      } catch {
        if (active) setStatus('error');
      }
    })();

    return () => {
      active = false;
    };
  }, [src, stripFirstH1]);

  if (status === 'loading') {
    return <div className="text-sm text-blackSecondary">Loading write-up…</div>;
  }

  if (status === 'error') {
    return (
      <p className="text-sm text-blackSecondary">
        Couldn't load the write-up.
      </p>
    );
  }

  /*
   * Fenced blocks: the `[&_pre_code]` rules reset the inline-code styling on
   * the nested <code>, otherwise its light background paints per line-box
   * inside the dark <pre> and the text disappears. Stacked prose-* variants
   * (`prose-pre:prose-code:*`) don't outrank the plugin's own selectors.
   */
  return (
    <div
      className="prose prose-neutral max-w-none prose-headings:font-medium prose-a:text-brand prose-a:no-underline hover:prose-a:underline prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-img:rounded-xl prose-code:rounded prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:before:content-none prose-code:after:content-none [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit [&_pre_code]:font-normal"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default Markdown;
